import { getDefaultWords } from '@/apis/s/s.api';
import SearchBtnSVG from '@/assets/icons/searchBtn.svg';
import { useTranslation } from '@/i18n/client';
import { useStores } from '@/models';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/23661';
import { eventTrackingUtils } from '@/utils/eventTrack/eventTrackingUtils';
import { spmTrackingUtils } from '@/intl-jdi-common-ssr/utils/spmTrackUtils';
import { isNextServer } from '@/intl-jdi-common-ssr/utils/uaParser';
import { observer } from 'mobx-react';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import styles from './searchBar.module.scss';
import {
  SEARCH_EVENT_NAMES,
  searchBusinessLogger,
} from '@/utils/logger/instance/searchBusinessLogger';
import SafeLink from '../../SafeLink';
import HEADER_PAGE_TYPE from '@/constants/header';

interface ISearchBar {
  disabled?: boolean;
  searchRef?: any;
  miniHomeModuleData?: any;
  poolData?: any;
  pageCode?: string;
  pageType?: string;
}

interface DefaultWordsItem {
  keyword: string;
  icon: string;
  landingPage: number;
  jumpPc: string;
  jumpMobile: string;
  extMap?: {
    algoid?: string;
    requestId?: string;
  };
}

const SearchBar: React.FC<ISearchBar> = observer(
  ({ searchRef, disabled, miniHomeModuleData, poolData, pageCode, pageType }) => {
    const { t } = useTranslation();
    const { searchStore } = useStores();
    const [keyword, setKeyword] = useState('');
    const [defaultWords, setDefaultWords] = useState<string[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [inputHref, setInputHref] = useState('');
    const [searchHref, setSearchHref] = useState('');
    const [extMap, setExtMap] = useState<any>({});
    const productPool = poolData?.dataBatch?.globalSetting?.[0]?.['page-product-pool'] || {};
    const poolIdList = productPool?.activityId;
    const tabName =
      miniHomeModuleData?.dataBatch?.tabList.find(
        (t: any) => t?.jumpLink?.jumpValue?.pageCodeInfo?.pageCode === pageCode,
      )?.tabName || '';
    const channelPageName = encodeURIComponent(tabName);

    useEffect(() => {
      const fetchDefaultWords = async () => {
        searchBusinessLogger({
          eventName: SEARCH_EVENT_NAMES.SEARCH_BACK_FETCH_START,
        });
        const defaultData = await getDefaultWords();
        const words =
          defaultData.data?.defaultWordsItems?.map((item: DefaultWordsItem) => item.keyword) || [];
        const extMapArray =
          defaultData.data?.defaultWordsItems?.map((item: DefaultWordsItem) => item.extMap) || [];
        if (defaultData.success && words.length > 0) {
          // 手动触发底纹曝光
          spmTrackingUtils.spmExpo({
            spm: 'a0145.b002218.c00002488.search_hints',
            page_id: 'Home_Main',
            event_id: 'Home_Search_Hints',
            json_param: JSON.stringify({
              index: 0,
              keyWord: words,
            }),
          });
        }
        if (words?.length === 0) {
          searchBusinessLogger({
            eventName: SEARCH_EVENT_NAMES.SEARCH_BACK_NO_DATA,
            message: {
              words,
            },
          });
        }
        setDefaultWords(words);
        setExtMap(extMapArray[0]);
      };
      if (!new URL(location.href).pathname.startsWith('/minihome/')) {
        fetchDefaultWords();
      }
    }, []);

    useEffect(() => {
      if (defaultWords.length === 0) return;
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % defaultWords.length);
      }, 1500);

      return () => clearInterval(timer);
    }, [defaultWords.length]);

    useEffect(() => {
      setInputHref(getInputHref());
      setSearchHref(getSearchHref());
    }, [defaultWords, currentIndex, keyword]);

    const handleInputChange = (e: any) => {
      setKeyword(e.target.value);
    };

    /**
     * 搜索按钮点击 Link href 跳转
     */
    const getSearchHref = () => {
      if (keyword.trim()) {
        return `/s?k=${encodeURIComponent(keyword.trim())}${
          channelPageName ? `&channelPageName=${channelPageName}` : ''
        }${poolIdList ? `&poolIdList=${poolIdList}` : ''}&st=active search&keywordsEventType=0`;
      }
      if (pageType !== HEADER_PAGE_TYPE.MINIHOME) {
        if (defaultWords.length > 0) {
          return `/s?k=${encodeURIComponent(defaultWords[currentIndex])}&dw=${
            defaultWords[currentIndex]
          }&st=hints&al=${extMap?.algoid || ''}&r=${extMap?.requestId || ''}&keywordsEventType=4`;
        }
      }
      return '';
    };

    /**
     * 用Link href 来跳转，这里只做其他逻辑（比如埋点）
     */
    const handleSearchClick = (e: any) => {
      if (keyword.trim()) {
        // window.location.href = getSearchHref();
        spmTrackingUtils.spmClick({
          spm: 'a0145.b002218.c00002488.searchbtn',
          event_id: 'Home_SearchBtn',
          page_id: 'Home_Main',
          target: e.target as HTMLElement,
          json_param: JSON.stringify({
            keyWord: keyword.trim(),
            searchtype: 'active search',
          }),
        });
      }
      if (pageType === HEADER_PAGE_TYPE.MINIHOME) {
        setDefaultWords([]);
        return;
      } else {
        if (defaultWords.length > 0) {
          // window.location.href = getSearchHref();
          //搜索按钮点击
          spmTrackingUtils.spmClick({
            spm: 'a0145.b002218.c00002488.searchbtn',
            event_id: 'Home_SearchBtn',
            page_id: 'Home_Main',
            target: e.target as HTMLElement,
            json_param: JSON.stringify({
              keyWord: defaultWords[currentIndex],
              searchtype: 'hints',
            }),
          });
        }
      }
    };

    /**
     * 搜索输入框点击 Link href 跳转
     */
    const getInputHref = () => {
      if (pageType === HEADER_PAGE_TYPE.HOME) {
        if (defaultWords[currentIndex]) {
          return `/s?dw=${defaultWords[currentIndex] || ''}&al=${extMap?.algoid || ''}&r=${
            extMap?.requestId || ''
          }`;
        } else {
          return `/s`;
        }
      } else if (pageType === HEADER_PAGE_TYPE.MINIHOME) {
        // 如果是小首页，直接跳转到小首页搜索页
        return `/s?channelPageName=${channelPageName}${
          poolIdList ? `&poolIdList=${poolIdList}` : ''
        }`;
      } else {
        return `/s`;
      }
    };

    return (
      <div className={`${styles.mainWrap} ${disabled ? styles.disabled : ''}`} ref={searchRef}>
        <div
          className={styles.inputWrap}
          data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
          data-exp={JSON.stringify({
            spm: 'a0145.b002218.c00002488.searchbox',
            page_id: 'Home_Main',
            event_id: 'Home_SearchBox_Expo',
          })}
        >
          {/* Link prefetch default true 优化跳转 */}
          <SafeLink
            href={inputHref}
            className={`${styles.linkWrap} ${styles.input}`}
            prefetch={true}
          >
            <input
              type='text'
              className={styles.input}
              onChange={handleInputChange}
              onClick={(e) => {
                spmTrackingUtils.spmClick({
                  spm: 'a0145.b002218.c00002488.searchbox',
                  page_id: 'Home_Main',
                  event_id: 'Home_SearchBox',
                  target: e.target as HTMLElement,
                });
              }}
              autoComplete='off'
              placeholder={defaultWords[currentIndex] || t('s.searchBar.placeholder')}
            />
          </SafeLink>
          <SafeLink href={searchHref} className={styles.linkWrap} prefetch={true}>
            <button
              className={styles.searchBtn}
              onClick={(e) => handleSearchClick(e)}
              aria-label='search button'
            >
              <SearchBtnSVG style={{ color: '#1A1A1A' }} />
            </button>
          </SafeLink>
        </div>
      </div>
    );
  },
);

export default SearchBar;
